import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { io, Socket } from 'socket.io-client';

const SocketContext = createContext<Socket | null>(null);

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    const socketUrl = process.env.GATSBY_SOCKET_URL;
    if (!socketUrl) {
      console.warn('[SocketProvider] GATSBY_SOCKET_URL not configured');
      return;
    }

    console.log('[SocketProvider] initializing socket connection to', socketUrl);

    const socketInstance = io(socketUrl, {
      transports: ['websocket'],
      withCredentials: true,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 500,
    });

    socketInstance.on('connect', () => {
      console.log('[SocketProvider] socket connected');
    });

    socketInstance.on('connect_error', (error) => {
      console.error('[SocketProvider] socket connect_error:', error.message);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('[SocketProvider] socket disconnected:', reason);
    });

    if (!socketInstance.connected) {
      socketInstance.connect();
    }

    setSocket(socketInstance);

    return () => {
      console.log('[SocketProvider] cleanup socket listeners');
      socketInstance.off('connect');
      socketInstance.off('connect_error');
      socketInstance.off('disconnect');
      socketInstance.disconnect();
      setSocket(null);
    };
  }, []);

  const value = useMemo(() => socket, [socket]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    console.warn('[useSocket] hook called outside of SocketProvider');
  }
  return context;
}
